import { useMemo } from "react";

import type { GscKeyword } from "../../types";

const CLOSEST_DEFAULT_VALUE = 200;

/**
 * Calculates slider tick values based on cumulative impressions percentages.
 * Adds a tick at each 1% threshold crossing (max ~100 ticks).
 * Returns ticks array and default value (closest to 200).
 */
export const useKeywordTicks = (keywords: GscKeyword[]) => {
    const ticks = useMemo(() => {
        const totalImpressions = keywords.reduce((sum, kw) => sum + kw.impressions, 0);
        if (totalImpressions === 0) {
            return [];
        }

        const result: number[] = [];
        let cumulativeImpressions = 0;
        let lastPercentage = 0;

        keywords.forEach((keyword, index) => {
            cumulativeImpressions += keyword.impressions;
            // Multiply before division to avoid floating-point boundary drift (e.g. 29/100*100 => 28.999...)
            const percentage = Math.floor((cumulativeImpressions * 100) / totalImpressions);
            if (percentage > lastPercentage) {
                result.push(index + 1); // 1-based count
                lastPercentage = percentage;
            }
        });

        return result;
    }, [keywords]);

    const defaultValue = useMemo(() => {
        if (ticks.length === 0) {
            return 0;
        }
        return ticks.reduce((prev, curr) =>
            Math.abs(curr - CLOSEST_DEFAULT_VALUE) < Math.abs(prev - CLOSEST_DEFAULT_VALUE) ? curr : prev,
        );
    }, [ticks]);

    return { ticks, defaultValue };
};
